import { VestingTerms } from '@dealstack/frontend/shared/components/VestingTerms/VestingTerms';
import { SecurityTableModal as SecuritiesTableModal } from '@dealstack/frontend/shared/components/SecurityTablesModal/SecurityTableModal';
import { LoanDefinitionTable } from '@dealstack/frontend/shared/components/LoanDefinitonTable/LoanDefinitionTable';
import { Button } from '@dealstack/frontend/shared/components/fundament/Button/Button';
import { Divider } from '@dealstack/frontend/shared/components/fundament/Divider/Divider';
import { SectionHeader } from '@dealstack/frontend/shared/components/SectionHeader/SectionHeader';
import { TagPair } from '@dealstack/frontend/shared/components/TagPair/TagPair';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns';
import { REPLAY_MASK_CLASS } from '@dealstack/frontend/shared/sentryPrivacy';
import classNames from 'classnames';
import styled from 'styled-components';
import { useMIPManageContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MIPManageContext';
import { NewJoinersAndAllocationsModalContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/NewJoinersAndAllocationsModal';
import { LoanDefinitionFormValues } from '@dealstack/frontend/shared/components/LoanDefinitonTable/useLoanDefinitionTableController';
import { SecuritiesFormValues } from '@dealstack/frontend/shared/components/SecuritiesTable/useSecuritiesTableController';
import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flex } from '@mantine/core';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils';

interface Props {
	className?: string;
}

export const NewJoinersAndAllocationsGeneralDealInputs = ({ className }: Props) => {
	const tMipManage = useTranslationMipManage();
	const navigate = useNavigate();

	const { deal, dealId, mipId } = useMIPManageContext();
	const { generalDealInputsForm, setDisableContinue, mipData, loanDefinitionForm } = useContext(
		NewJoinersAndAllocationsModalContext
	);

	useEffect(() => {
		const securitiesFields = generalDealInputsForm.getValues(SecuritiesFormValues.securities);
		const securitiesFieldsOnEditionMode = securitiesFields.filter((field) => field.isOnEdit);
		const loanDefinitionFields = loanDefinitionForm.getValues(
			LoanDefinitionFormValues.loanDefinitions
		);
		const loanDefinitionFieldsOnEditionMode = loanDefinitionFields.filter(
			(field) => field.isOnEdit
		);

		const shouldDisableContinue =
			securitiesFieldsOnEditionMode.length > 0 ||
			loanDefinitionFieldsOnEditionMode.length > 0;
		setDisableContinue(shouldDisableContinue);
		return () => {
			setDisableContinue(false);
		};
	}, [
		generalDealInputsForm.watch(SecuritiesFormValues.securities),
		generalDealInputsForm.formState,
		loanDefinitionForm.watch(LoanDefinitionFormValues.loanDefinitions),
		loanDefinitionForm.formState,
	]);

	const onEditProjectClick = () => {
		const path = mipManagePath(MIP_MANAGE_ROUTES.SETTINGS, {
			dealId: dealId ?? '',
			mipId: mipId ?? '',
		});
		navigate(path);
	};

	const securities = generalDealInputsForm.getValues(SecuritiesFormValues.securities);

	const anySecurityHasSubjectToVesting = securities?.some(
		(security) => security.isSubjectToVesting
	);

	return (
		<div className={className}>
			<Flex justify="space-between" gap="1.5em" mb="1em">
				<SectionHeader
					size="h4b"
					title={tMipManage(
						'investors.upsert_investor_shares_modal.general_deal_inputs.header.title'
					)}
					subtitle={tMipManage(
						'investors.upsert_investor_shares_modal.general_deal_inputs.header.subtitle'
					)}
				/>
				<Button
					variant="outlined"
					iconPlacement="left"
					icon="edit-pencil"
					iconSize="lg"
					onClick={onEditProjectClick}
				>
					{tMipManage('investors.upsert_investor_shares_modal.edit')}
				</Button>
			</Flex>

			<SectionHeader
				title={tMipManage(
					'investors.upsert_investor_shares_modal.general_deal_inputs.deal_and_target'
				)}
				size="h5b"
			/>
			<TagsGroupRow>
				<TagPair
					title={tMipManage(
						'investors.upsert_investor_shares_modal.general_deal_inputs.target_name'
					)}
				>
					{deal?.targetName}
				</TagPair>
				<TagPair
					title={tMipManage(
						'investors.upsert_investor_shares_modal.general_deal_inputs.project_name'
					)}
				>
					{deal?.name || LONG_DASH}
				</TagPair>
			</TagsGroupRow>

			<SectionHeader
				title={tMipManage(
					'investors.upsert_investor_shares_modal.general_deal_inputs.about_the_offer'
				)}
				size="h5b"
			/>
			<TagsGroupRow>
				<TagPair
					title={tMipManage(
						'investors.upsert_investor_shares_modal.general_deal_inputs.entity_name'
					)}
				>
					{mipData?.entity?.displayName || LONG_DASH}
				</TagPair>
				<TagPair
					title={tMipManage(
						'investors.upsert_investor_shares_modal.general_deal_inputs.currency'
					)}
				>
					{mipData?.currency || LONG_DASH}
				</TagPair>
			</TagsGroupRow>

			<SectionHeader
				title={tMipManage(
					'investors.upsert_investor_shares_modal.general_deal_inputs.payment_instructions'
				)}
				size="h5b"
			/>
			<TagsGroupRow>
				<TagPair
					title={tMipManage(
						'investors.upsert_investor_shares_modal.general_deal_inputs.account_holder'
					)}
				>
					{mipData?.bankAccount?.accountHolder || LONG_DASH}
				</TagPair>
				<TagPair
					title={tMipManage(
						'investors.upsert_investor_shares_modal.general_deal_inputs.bank_name_label'
					)}
				>
					{mipData?.bankAccount?.bankName || LONG_DASH}
				</TagPair>
				<TagPair
					title={tMipManage(
						'investors.upsert_investor_shares_modal.general_deal_inputs.swift_label'
					)}
				>
					{mipData?.bankAccount?.swiftCode || LONG_DASH}
				</TagPair>
			</TagsGroupRow>
			<TagsGroupRow>
				<TagPair
					title={tMipManage(
						'investors.upsert_investor_shares_modal.general_deal_inputs.iban_label'
					)}
				>
					<span className={REPLAY_MASK_CLASS} data-sentry-mask>
						{mipData?.bankAccount?.iban || LONG_DASH}
					</span>
				</TagPair>
				<TagPair
					title={tMipManage(
						`investors.upsert_investor_shares_modal.general_deal_inputs.instructions_label`
					)}
				>
					{mipData?.bankAccount?.investorInstructions || LONG_DASH}
				</TagPair>
			</TagsGroupRow>

			<Flex direction="column" gap="1.5rem">
				<SecuritiesOnOfferHeader
					title={tMipManage('investors.upsert_investor_shares_modal.securities_on_offer')}
					size="h5b"
				/>
				<SecuritiesTableModal securitiesForm={generalDealInputsForm} />

				<Divider />
				<SectionHeader
					title={tMipManage('investors.upsert_investor_shares_modal.loans_on_offer')}
					size="h5b"
				/>
				<LoanDefinitionTable loanDefinitionForm={loanDefinitionForm} />
			</Flex>
			<div
				className={classNames('mt-4', {
					'opacity-50': !anySecurityHasSubjectToVesting,
					'pointer-events-none': !anySecurityHasSubjectToVesting,
				})}
			>
				<div className="my-4 mb-20">
					<VestingTerms />
				</div>
			</div>
		</div>
	);
};

const TagsGroupRow = styled.div`
	display: flex;
	gap: 2.5em;
	margin: 1em 0;

	& > * {
		min-width: 16em;
	}
`;
const SecuritiesOnOfferHeader = styled(SectionHeader)`
	margin-top: 2em;
`;
